import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";

import CustomSpinner from "../../components/customSpinner/CustomSpinner";
import { useHotelDataContext } from "../../hooks/HotelDataContextHook";
import FilterBar from "./components/filterBar/FilterBar";
import styles from "./Home.module.scss";

import PropertyCardWrapper from "./components/propertyCard/components/propertyCardWrapper/PropertyCardWrapper";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import UpdateUrlParams from "../../hooks/UpdateUrlParams";
import {
  useUbicationFilterContext,
  zoneI,
} from "../../hooks/UbicationFilterContext";
import useQuery from "../../hooks/useQuery";
import Mapview from "./components/mapview/Mapview";
import {
  selectCouponCode,
  selectEndDate,
  selectIniDate,
  selectSearchHotel,
} from "../../redux/slices/enviromentSlice/EnviromentSlice.selectors";
import {
  setCouponCode,
  setEnviroment,
} from "../../redux/slices/enviromentSlice/EnviromentSlice";
import moment from "moment";
import { selectedDatesI } from "../../components/topBar/components/calendarDropdown/CalendarDropdown";
import TopBar from "../../components/topBar/TopBar";
import { getUbication } from "../../utils/getUbication";
import { DEFAULT_ADULTS } from "../../defaultVars";
import { APP_PAGES } from "../../App";
import useSentryUtils from "../../hooks/useSentryUtils";
import { FilterSortType, filterStateI } from "./contexts/filterContext";
import { useWidgetConfigContext } from "src/contexts/widgetConfig/WidgetConfig.context";

const Home: React.FC<{ environmentId: string }> = (props) => {
  const { environmentId } = props;

  // redux hooks
  const dispatch = useAppDispatch();
  const couponCode = useAppSelector(selectCouponCode);
  const iniDate = useAppSelector(selectIniDate);
  const endDate = useAppSelector(selectEndDate);
  const searchHotel = useAppSelector(selectSearchHotel);

  const { defaultMargin, defaultNights } = useWidgetConfigContext();

  const queryParams = useQuery();

  const { updateUrlParams } = UpdateUrlParams();

  const {
    fetchHotelDataList,
    filteredHotelDataList,
    hotelDataList,
    loading: hotelLoading,
    nights,
  } = useHotelDataContext();
  const { ubicationFilterList } = useUbicationFilterContext();
  const { t } = useTranslation();

  const { captureError } = useSentryUtils();

  const urlAdults = queryParams.get("adults");
  const urlZoneId = queryParams.get("zoneId");
  const urlSearchHotel = queryParams.get("searchHotel");
  const urlChildrenAges =
    queryParams
      .get("childrenAges")
      ?.split(",")
      .map((age) => +age) || [];

  const [filterState, setFilterState] = useState<filterStateI>({
    orderBy: FilterSortType.ASCENDING,
    isFreeCancelable: FilterSortType.UNSET,
    priceRange: undefined,
    amenitiesIds: [],
    hotelTypeIds: [],
  });
  const [isMapviewOpen, setIsMapviewOpen] = useState(false);
  const [childrenAgeList, setChildrenAgeList] = useState<Array<number>>(
    urlChildrenAges || []
  );
  const [currentAdults, setCurrentAdults] = useState(
    urlAdults ? +urlAdults : DEFAULT_ADULTS
  );
  const [selectedDates, setSelectedDates] = useState<selectedDatesI>({
    startDate: iniDate ? moment(iniDate, "DD-MM-YYYY") : moment(),
    endDate: endDate
      ? moment(endDate, "DD-MM-YYYY")
      : moment().add(defaultNights, "days"),
  });
  const [selectedZone, setSelectedZone] = useState<zoneI>(
    getUbication(ubicationFilterList, urlZoneId)
  );

  useEffect(() => {
    if (iniDate)
      setSelectedDates((currentSelectedDates) => ({
        ...currentSelectedDates,
        startDate: moment(iniDate, "DD-MM-YYYY"),
      }));
  }, [iniDate]);

  useEffect(() => {
    if (endDate)
      setSelectedDates((currentSelectedDates) => ({
        ...currentSelectedDates,
        endDate: moment(endDate, "DD-MM-YYYY"),
      }));
  }, [endDate]);

  useEffect(() => {
    (() => {
      // updateUrlParams({ updateAll: true, replace: true });
      fetchHotelDataList({
        zoneId: selectedZone.id,
        adults: currentAdults,
        childrenAges: childrenAgeList,
        iniDate: selectedDates.startDate?.format("DD-MM-YYYY"),
        endDate: selectedDates.endDate?.format("DD-MM-YYYY"),
      }).catch((error) => {
        console.log(error);
        captureError(error, {
          description: "error from useEffect fetchHotelDataList in Home.tsx",
        });
        updateUrlParams({
          updatePage: APP_PAGES.ERROR_PAGE,
        });
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeUbication = (ubicationFilter: zoneI) => {
    setSelectedZone(ubicationFilter);
  };

  const handleDateChange = (newDates: selectedDatesI) => {
    setSelectedDates({
      startDate: newDates.startDate,
      endDate: newDates.endDate,
    });
  };

  const handleChildrenChange = (childrenAges: number[]) => {
    setChildrenAgeList(childrenAges);
  };

  const handleAdultsChange = (adults: number) => {
    setCurrentAdults(adults);
  };

  const handleSearch = () => {
    const newChildrenAgeList = childrenAgeList.map((children) => children);
    const newIniDate = selectedDates.startDate?.format("DD-MM-YYYY");
    const newEndDate = selectedDates.endDate?.format("DD-MM-YYYY");

    dispatch(
      setEnviroment({
        childrenAgeList: newChildrenAgeList,
        adults: currentAdults,
        iniDate: newIniDate,
        endDate: newEndDate,
        zoneId: selectedZone.id,
        couponCode: couponCode,
      })
    );
    dispatch(setCouponCode(couponCode));
    updateUrlParams({
      updateChildrenAgeList: newChildrenAgeList,
      updateAdults: currentAdults,
      updateIniDate: newIniDate,
      updateEndDate: newEndDate,
      updateZoneId: selectedZone.id,
      updateSearchHotel: searchHotel,
    });
    fetchHotelDataList({
      childrenAges: newChildrenAgeList,
      adults: currentAdults,
      iniDate: newIniDate,
      endDate: newEndDate,
      zoneId: selectedZone.id,
      couponCode: couponCode,
    });
  };

  const searchHotelName =
    urlSearchHotel &&
    hotelDataList.find(({ id }) => id === urlSearchHotel)?.name;

  return (
    <>
      <div className={styles.Home_mainContainer}>
        <div
          className={`${styles.Home} ${
            defaultMargin ? styles.defaultMargin : ""
          }`}
        >
          <div>
            <TopBar
              ubication={selectedZone}
              dates={selectedDates}
              adults={currentAdults}
              childrenAges={childrenAgeList}
              onChangeUbication={handleChangeUbication}
              onChangeDate={handleDateChange}
              onChangeChildren={handleChildrenChange}
              onChangeAdults={handleAdultsChange}
              onSearch={handleSearch}
            />
            <div className={styles.searchLocation}>
              <span className={styles.searchLocation_label}>
                {t("labels.searchedLocation")}
              </span>
              <span
                className={`${styles.searchLocation_location} home__searchedLocationLabel`}
              >
                {searchHotelName ||
                  getUbication(ubicationFilterList, urlZoneId).name}
              </span>
            </div>
            <FilterBar
              filterState={filterState}
              setFilterState={setFilterState}
              isMapviewOpen={isMapviewOpen}
              setIsMapviewOpen={setIsMapviewOpen}
            />
          </div>
          <div className={`${isMapviewOpen && styles.Home_wrapper_bottom}`}>
            {!isMapviewOpen && (
              <div className={styles.propertyCardContainer}>
                <p
                  className={`${styles.propertyCardTitle} home__featuredEstablishmentsLabel`}
                >
                  {t("labels.featuredEstablishments")}
                </p>
              </div>
            )}
            {!hotelLoading ? (
              isMapviewOpen ? (
                <Mapview
                  filterState={filterState}
                  hotelDataList={
                    filteredHotelDataList &&
                    filteredHotelDataList.map((hotel) => ({
                      ...hotel,
                      nights,
                    }))
                  }
                />
              ) : (
                <div className={styles.propertyCardContainer}>
                  <PropertyCardWrapper filterState={filterState}>
                    {filteredHotelDataList &&
                      filteredHotelDataList.map((hotel) => ({
                        ...hotel,
                        nights,
                      }))}
                  </PropertyCardWrapper>
                </div>
              )
            ) : (
              <div className={styles.customSpinnerContainer}>
                <CustomSpinner />
              </div>
            )}
          </div>
        </div>
      </div>
      {!environmentId && (
        <div className={styles.noEnviroment_mainContainer}>
          <div className={styles.noEnviroment}>{t("errors.no_enviroment")}</div>
        </div>
      )}
    </>
  );
};

export default Home;
